import { jsx } from "react/jsx-runtime";
import KatexRenderer from "./KatexRenderer.js";
const FormattedText = ({ text, className = "", vars }) => {
  if (!text) return null;
  let normalizedText = text.normalize ? text.normalize("NFC") : text;
  if (vars && Object.keys(vars).length > 0) {
    normalizedText = substituteVariables(normalizedText, vars);
  } else {
    normalizedText = preprocessLegacyMarkup(normalizedText);
  }
  return /* @__PURE__ */ jsx("div", { className, children: /* @__PURE__ */ jsx(KatexRenderer, { text: normalizedText }) });
};
const getVarValue = (vars, key, index) => {
  const variable = vars[key];
  if (variable === void 0 || variable === null) return void 0;
  if (index !== void 0) {
    const resolvedIndex = typeof index === "number" ? index : resolveArrayIndex(vars, index);
    if (resolvedIndex === void 0) return void 0;
    if (typeof variable === "object" && "array" in variable && Array.isArray(variable.array)) {
      return variable.array[resolvedIndex];
    }
    if (Array.isArray(variable)) {
      return variable[resolvedIndex];
    }
    if (typeof variable === "object" && "result" in variable && Array.isArray(variable.result)) {
      return variable.result[resolvedIndex];
    }
  } else {
    if (typeof variable === "number" || typeof variable === "string") {
      return variable;
    }
    if (typeof variable === "object" && "result" in variable && variable.result !== void 0) {
      const result = variable.result;
      if (typeof result === "number") {
        return Number.isInteger(result) ? Math.floor(result) : result;
      }
      return result;
    }
  }
  return void 0;
};
const resolveArrayIndex = (vars, index) => {
  const trimmed = index.trim();
  if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
  const nestedArrayMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\[([^\]]+)\]$/);
  const value = nestedArrayMatch ? getVarValue(vars, nestedArrayMatch[1], nestedArrayMatch[2]) : getVarValue(vars, trimmed);
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? void 0 : parsed;
  }
  return void 0;
};
const evaluateExpression = (expr, vars) => {
  try {
    let substituted = expr.replace(
      /([a-zA-Z_][a-zA-Z0-9_]*)\[([^\]]+)\]/g,
      (match, key, indexStr) => {
        const value = getVarValue(vars, key, indexStr);
        return value !== void 0 ? String(value) : match;
      }
    );
    substituted = substituted.replace(/([a-zA-Z_][a-zA-Z0-9_]*)/g, (match, key) => {
      if (/^\d/.test(match)) return match;
      const value = getVarValue(vars, key);
      return value !== void 0 ? String(value) : match;
    });
    if (/[a-zA-Z_]/.test(substituted.replace(/\d+\.?\d*/g, ""))) {
      return null;
    }
    const result = new Function(`return (${substituted})`)();
    if (typeof result === "number") {
      if (Number.isInteger(result)) {
        return String(result);
      }
      return String(Math.round(result * 1e6) / 1e6);
    }
    return String(result);
  } catch {
    return null;
  }
};
const replaceSquareBracketPattern = (str) => {
  const regex = /\[\](\{\{.*?\}\})/g;
  return str.replace(regex, "$1");
};
const preprocessLegacyMarkup = (text) => {
  if (!text) return text || "";
  let processed = text;
  processed = replaceSquareBracketPattern(processed);
  processed = processed.replace(/＋/g, "+").replace(/－/g, "-").replace(/＝/g, "=").replace(/＜/g, "<").replace(/＞/g, ">").replace(/（/g, "(").replace(/）/g, ")").replace(/［/g, "[").replace(/］/g, "]").replace(/｛/g, "{").replace(/｝/g, "}").replace(/−/g, "-").replace(/–/g, "-").replace(/—/g, "-").replace(/′/g, "'").replace(/″/g, "''").replace(/'/g, "'").replace(/'/g, "'").replace(/"/g, '"').replace(/"/g, '"').replace(/…/g, "...").replace(/％/g, "\\%").replace(/𝑚/g, "m").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"');
  processed = processed.replace(/\$\$(.*?)\$\$/g, (_, content) => {
    const converted = content.replace(/×/g, "\\times").replace(/÷/g, "\\div");
    return `$$${converted}$$`;
  });
  processed = processed.replace(/<frac>([\s\S]*?)<\/frac>/gi, (match, content) => {
    let separatorIndex = -1;
    let depth = 0;
    for (let i = 0; i < content.length - 1; i++) {
      if (content[i] === "{" && content[i + 1] === "{") depth++;
      if (content[i] === "}" && content[i + 1] === "}") {
        depth--;
        i++;
        continue;
      }
      if (depth === 0 && content[i] === "/" && content[i + 1] === "/") {
        separatorIndex = i;
        break;
      }
    }
    if (separatorIndex === -1) return match;
    const num = content.substring(0, separatorIndex).trim();
    const den = content.substring(separatorIndex + 2).trim();
    const cleanNum = num.replace(/\$\$/g, "").trim();
    const cleanDen = den.replace(/\$\$/g, "").trim();
    const encodedNum = cleanNum.replace(/\{\{/g, "__LBLANK__").replace(/\}\}/g, "__RBLANK__");
    const encodedDen = cleanDen.replace(/\{\{/g, "__LBLANK__").replace(/\}\}/g, "__RBLANK__");
    return `[FRAC:${encodedNum}:/:${encodedDen}:FRAC]`;
  });
  processed = processed.replace(/<sqrt>\s*(.*?)\s*<\/sqrt>/gi, (_, content) => {
    const cleanContent = content.replace(/\$\$/g, "").trim();
    return `$$\\sqrt{${cleanContent}}$$`;
  });
  processed = processed.replace(/<sup>\s*(.*?)\s*<\/sup>/gi, (_, content) => {
    const cleanContent = content.replace(/\$\$/g, "").trim();
    return `$$^{${cleanContent}}$$`;
  });
  processed = processed.replace(/<sub>\s*(.*?)\s*<\/sub>/gi, (_, content) => {
    const cleanContent = content.replace(/\$\$/g, "").trim();
    return `$$_{${cleanContent}}$$`;
  });
  processed = processed.replace(/\\hspace(\d+\.?\d*)([a-z]+)/g, "\\hspace{$1$2}");
  return processed;
};
const substituteVariables = (text, vars) => {
  if (!vars || !text) return text || "";
  const typedVars = vars;
  let processed = preprocessLegacyMarkup(text);
  processed = processed.replace(/#\{([^}]+)\}/g, (match, content) => {
    const trimmed = content.trim();
    const simpleArrayMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)\[([^\]]+)\]$/);
    if (simpleArrayMatch) {
      const [, key, indexStr] = simpleArrayMatch;
      const value = getVarValue(typedVars, key, indexStr);
      if (value !== void 0) {
        return String(value);
      }
      return match;
    }
    const simpleVarMatch = trimmed.match(/^([a-zA-Z_][a-zA-Z0-9_]*)$/);
    if (simpleVarMatch) {
      const value = getVarValue(typedVars, trimmed);
      if (value !== void 0) {
        return String(value);
      }
      return match;
    }
    const result = evaluateExpression(trimmed, typedVars);
    if (result !== null) {
      return result;
    }
    return match;
  });
  return processed;
};
export {
  FormattedText,
  preprocessLegacyMarkup,
  replaceSquareBracketPattern,
  substituteVariables
};
//# sourceMappingURL=textFormatter.js.map
